<script lang="ts" setup>
import { computed, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import {
  useDeadLetters,
  useDiscardDeadLetters,
  DEAD_LETTER_DEFAULT_TAKE,
  DEAD_LETTER_WINDOW_OPTIONS,
  type DeadLetter,
} from "@/queries/dead-letters";

// Every org, so the filter still lists a tenant after the feed has been narrowed to another.
interface OrgListItem { orgId: string; orgName: string }
const { data: orgsData } = useQuery({
  queryKey: ["admin-dead-letter-orgs"],
  queryFn: () => fetchWrapper.get<OrgListItem[]>(`${import.meta.env.VITE_API_URL}/orgs`),
});
const orgs = computed(() => orgsData.value ?? []);
const orgName = (orgId: string) => orgs.value.find((o) => o.orgId === orgId)?.orgName ?? orgId;

const selectedOrgId = ref("");
const serverOrgId = computed(() => selectedOrgId.value || undefined);

// The estate read crosses every partition, so it is bounded. Days and rows are both the admin's to widen.
const days = ref(7);
const take = ref(DEAD_LETTER_DEFAULT_TAKE);

const { deadLetters, deadLettersLoading, refetchDeadLetters } = useDeadLetters({
  orgId: serverOrgId,
  days,
  take,
});
const { discardDeadLetters, discardPending } = useDiscardDeadLetters();

// The list is a limit, not a page, so a full one means there may be more — say so rather than imply the
// estate holds exactly this many.
const capped = computed(() => deadLetters.value.length >= take.value);

const expanded = ref<string | null>(null);
const toggle = (id: string) => (expanded.value = expanded.value === id ? null : id);

const selected = ref<Set<string>>(new Set());
const isSelected = (id: string) => selected.value.has(id);

function select(id: string, on: boolean) {
  const next = new Set(selected.value);
  on ? next.add(id) : next.delete(id);
  selected.value = next;
}

// Discarding is addressed by partition, so a selection spanning two orgs is two calls. Selecting across
// orgs is legitimate from the god-view, so the action groups rather than refusing.
const selectedByOrg = computed(() => {
  const byOrg = new Map<string, string[]>();
  for (const record of deadLetters.value) {
    if (!isSelected(record.id)) continue;
    byOrg.set(record.orgId, [...(byOrg.get(record.orgId) ?? []), record.id]);
  }
  return byOrg;
});

const confirming = ref(false);

async function discard() {
  for (const [orgId, ids] of selectedByOrg.value) await discardDeadLetters({ orgId, ids });
  selected.value = new Set();
  confirming.value = false;
}

/** The run this record came from, where it named one and the run is still readable. */
function runLink(record: DeadLetter): string | null {
  return record.projectId && record.workflowId && record.workflowRunId
    ? `/projects/${record.projectId}/history/${record.workflowId}/${record.workflowRunId}`
    : null;
}

const recordedAt = (iso: string) => new Date(iso).toLocaleString();
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Dead letters"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Admin' },
          { label: 'Dead letters' },
        ]"
      />

      <p class="text-muted mt-3 mb-4">
        Messages the broker gave up on after every retry. Normally there are none — anything here means a
        run stopped and this record is the only thing that says why. Discarding one removes the last
        evidence of it; the message itself is long gone.
      </p>

      <div class="d-flex flex-wrap align-items-end gap-3 mb-4">
        <div style="min-width: 16rem">
          <label class="form-label">Org</label>
          <select v-model="selectedOrgId" class="form-select">
            <option value="">All orgs</option>
            <option v-for="org in orgs" :key="org.orgId" :value="org.orgId">{{ org.orgName }}</option>
          </select>
        </div>

        <div style="min-width: 11rem">
          <label class="form-label">Recorded</label>
          <select v-model.number="days" class="form-select">
            <option v-for="option in DEAD_LETTER_WINDOW_OPTIONS" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div style="min-width: 7rem">
          <label class="form-label">Rows</label>
          <select v-model.number="take" class="form-select">
            <option :value="100">100</option>
            <option :value="500">500</option>
            <option :value="2000">2000</option>
          </select>
        </div>

        <button class="bb-btn-secondary-cta" :disabled="deadLettersLoading" @click="refetchDeadLetters()">
          <i class="bi bi-arrow-clockwise me-1"></i>Refresh
        </button>

        <div v-if="selected.size > 0" class="ms-auto d-flex align-items-center gap-2">
          <template v-if="confirming">
            <span class="small text-muted">Discard {{ selected.size }}? This cannot be undone.</span>
            <button class="btn btn-danger btn-sm" :disabled="discardPending" @click="discard">
              Confirm
            </button>
            <button class="bb-btn-secondary-cta btn-sm" @click="confirming = false">Cancel</button>
          </template>
          <button v-else class="btn btn-outline-danger btn-sm" @click="confirming = true">
            <i class="bi bi-trash me-1"></i>Discard {{ selected.size }}
          </button>
        </div>
      </div>

      <div v-if="deadLettersLoading && deadLetters.length === 0" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading…</span>
        </div>
      </div>

      <div v-else-if="deadLetters.length === 0" class="text-center text-muted py-5">
        <i class="bi bi-check-circle fs-3 d-block mb-2"></i>
        Nothing has been dead-lettered.
      </div>

      <div v-else>
        <p v-if="capped" class="small text-muted">
          Showing the most recent {{ take }}. There may be more — narrow to one org or a shorter window.
        </p>

        <div class="table-responsive">
        <table class="table align-middle">
          <thead>
            <tr>
              <th style="width: 2rem"></th>
              <th>Recorded</th>
              <th>Org</th>
              <th>Queue</th>
              <th>Message</th>
              <th>Reason</th>
              <th>Run</th>
              <th style="width: 2rem"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="record in deadLetters" :key="record.id">
              <tr>
                <td>
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="isSelected(record.id)"
                    @change="select(record.id, ($event.target as HTMLInputElement).checked)"
                  />
                </td>
                <td class="text-nowrap small">{{ recordedAt(record.recordedAt) }}</td>
                <td><span class="badge bg-dark">{{ orgName(record.orgId) }}</span></td>
                <td class="small text-muted">{{ record.entity }}</td>
                <td>
                  <span :class="record.eventType === 'Unreadable' ? 'fst-italic text-warning' : ''">
                    {{ record.eventType }}
                  </span>
                </td>
                <td class="small">{{ record.reason }}</td>
                <td class="small">
                  <router-link v-if="runLink(record)" :to="runLink(record)!">
                    {{ record.workflowName }}
                  </router-link>
                  <span v-else-if="record.workflowRunId" class="text-muted" :title="record.workflowRunId">
                    run no longer readable
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <button class="btn btn-sm btn-link p-0" @click="toggle(record.id)">
                    <i :class="expanded === record.id ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="expanded === record.id">
                <td colspan="8" class="bg-body-tertiary">
                  <dl class="row small mb-2">
                    <dt class="col-sm-2">Description</dt>
                    <dd class="col-sm-10">{{ record.description }}</dd>
                    <dt class="col-sm-2">Service</dt>
                    <dd class="col-sm-10">{{ record.serviceId ?? "—" }}</dd>
                    <dt class="col-sm-2">Run status</dt>
                    <dd class="col-sm-10">{{ record.runStatus ?? "—" }}</dd>
                    <dt class="col-sm-2">Record</dt>
                    <dd class="col-sm-10 font-monospace">{{ record.id }}</dd>
                  </dl>
                  <label class="form-label small">Message body</label>
                  <pre class="small mb-0 p-3 bg-body rounded border overflow-auto" style="max-height: 20rem">{{ record.body }}</pre>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        </div>
      </div>
    </PanelCard>
  </PanelGrid>
</template>
